/**
 * What the BMP-map gate will take, and what to tell the inspector when it
 * will not. Staging 2026-09-30: five uploads were refused, the gate said only
 * "Upload failed — try again", and an inspector bypassed it with "Won't load".
 */

const EXT_TYPE: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.tif': 'image/tiff',
  '.tiff': 'image/tiff',
  '.bmp': 'image/bmp',
  '.pdf': 'application/pdf',
};

export const MAX_UPLOAD_MB = 15;
export const ACCEPTED_HINT = 'Take a photo, or attach a JPG, PNG, HEIC or PDF.';

const extension = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};

/** The file's real type: the browser's, or the extension's when it gave none. */
export function uploadType(file: Pick<File, 'name' | 'type'>): string {
  const declared = (file.type || '').toLowerCase();
  if (declared && declared !== 'application/octet-stream') return declared;
  return EXT_TYPE[extension(file.name)] ?? '';
}

/**
 * Why a picked file cannot be a BMP map, or null when it can. Checked before
 * anything is sent, so a wrong pick costs a second, not a 35-second upload.
 */
export function uploadProblem(
  file: Pick<File, 'name' | 'type' | 'size'>
): string | null {
  const type = uploadType(file);
  if (!type.startsWith('image/') && type !== 'application/pdf') {
    const kind = extension(file.name) || file.type || 'unknown type';
    return `${file.name} is not a photo or a PDF (${kind}). ${ACCEPTED_HINT}`;
  }
  // Photos are re-saved smaller on the server; a PDF goes up as it is.
  if (type === 'application/pdf' && file.size > MAX_UPLOAD_MB * 1024 * 1024) {
    return `${file.name} is over ${MAX_UPLOAD_MB} MB. Take a photo of the map, or attach a smaller PDF.`;
  }
  return null;
}

/** Give a blank-typed file the type its extension implies, so the server sees it. */
export function withUploadType(file: File): File {
  const type = uploadType(file);
  if (!type || type === file.type) return file;
  return new File([file], file.name, { type });
}

/** One line for the inspector from whatever the upload threw. */
export function describeUploadError(error: unknown): string {
  if (error instanceof UploadRefused) return error.message;
  const response = (
    error as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  if (!response) {
    return 'Could not reach the server. Check your signal and try again — the map was not saved.';
  }
  if (typeof response.data?.detail === 'string' && response.data.detail) {
    return response.data.detail;
  }
  if (response.status === 413) {
    return `That file is over ${MAX_UPLOAD_MB} MB. Take a photo of the map, or attach a smaller PDF.`;
  }
  return 'The map could not be saved. Try again, or take a photo of it from this screen.';
}

/** Thrown before any request when a picked file fails {@link uploadProblem}. */
export class UploadRefused extends Error {}

// Phones (esp. iPhones) hand us HEIC; the API only accepts JPEG/PNG. Re-encode
// via canvas when the browser can decode it; fall back to the original file.
// Non-images (a PDF map straight from the engineer) pass straight through.
//
// Jim 2026-10-14: a map photographed in landscape uploaded sideways. Phones store
// the rotation as an EXIF flag and leave the pixels as shot; anything that ignores
// the flag shows the photo on its side. Every photo now goes through the canvas with
// the rotation applied to the pixels, JPEGs included (PNGs keep their format).
export async function toUploadableImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/png') return file;
  try {
    const bmp = await createImageBitmap(file, {
      imageOrientation: 'from-image',
    });
    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    canvas.getContext('2d')!.drawImage(bmp, 0, 0);
    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob(res, 'image/jpeg', 0.85)
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
      type: 'image/jpeg',
    });
  } catch {
    return file;
  }
}
